<script setup>
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Slider from 'dashboard/components-next/slider/Slider.vue';
import QueryBuilderInstructionEditor from './components/QueryBuilderInstructionEditor.vue';
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { required, maxLength } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { isCloudFeatureEnabled } = useAccount();
const eventDocumentsEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING_EVENT_DOCUMENTS)
);

const props = defineProps({
  active: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['close', 'cancel']);

const store = useStore();
const { t } = useI18n();

const name = ref('');
const description = ref('');
const type = ref('documents'); // default type — also supports event_documents (file library, no indexing)

const queryBuilderInstructions = ref('');
const optimizedQuery = ref(false);
const disableMatchFilter = ref(false);
const indexMedia = ref(false);
const hybridFactor = ref(1.5);
const maxDistanceBoost = ref(0.5);
const defaultGeoMode = ref(null);
const showAdvanced = ref(false);

const hybridFactorLevel = computed(() => {
  if (hybridFactor.value <= 1.0)
    return t('LIBRARIES.ADD.ADVANCED_SETTINGS.LEVELS.NONE');
  if (hybridFactor.value <= 1.3)
    return t('LIBRARIES.ADD.ADVANCED_SETTINGS.LEVELS.LOW');
  if (hybridFactor.value <= 1.6)
    return t('LIBRARIES.ADD.ADVANCED_SETTINGS.LEVELS.MEDIUM');
  return t('LIBRARIES.ADD.ADVANCED_SETTINGS.LEVELS.HIGH');
});

const maxDistanceBoostLevel = computed(() => {
  if (maxDistanceBoost.value <= 0.0)
    return t('LIBRARIES.ADD.ADVANCED_SETTINGS.LEVELS.NONE');
  if (maxDistanceBoost.value <= 0.3)
    return t('LIBRARIES.ADD.ADVANCED_SETTINGS.LEVELS.LOW');
  if (maxDistanceBoost.value <= 0.6)
    return t('LIBRARIES.ADD.ADVANCED_SETTINGS.LEVELS.MEDIUM');
  return t('LIBRARIES.ADD.ADVANCED_SETTINGS.LEVELS.HIGH');
});

const v$ = useVuelidate(
  {
    name: { required },
    description: { required },
    type: { required },
    queryBuilderInstructions: { maxLength: maxLength(1000) },
  },
  { name, description, type, queryBuilderInstructions, disableMatchFilter }
);

const resetForm = () => {
  name.value = '';
  description.value = '';
  type.value = 'documents';
  queryBuilderInstructions.value = '';
  optimizedQuery.value = false;
  disableMatchFilter.value = false;
  indexMedia.value = false;
  hybridFactor.value = 1.5;
  maxDistanceBoost.value = 0.5;
  defaultGeoMode.value = null;
  showAdvanced.value = false;
  v$.value.$reset();
};

const addKnowledgebase = async () => {
  v$.value.$touch();

  if (v$.value.$invalid) {
    return;
  }

  try {
    await store.dispatch('knowledgebase/create', {
      knowledgebase: {
        name: name.value,
        description: description.value,
        type: type.value,
        query_builder_instructions: queryBuilderInstructions.value,
        optimized_query: optimizedQuery.value,
        disable_match_filter: disableMatchFilter.value,
        index_media: indexMedia.value,
        hybrid_factor: hybridFactor.value,
        max_distance_boost: maxDistanceBoost.value,
        default_geo_mode: defaultGeoMode.value,
      },
    });

    useAlert(t('LIBRARIES.ADD.API.SUCCESS_MESSAGE'));
    resetForm();
    emit('close');
  } catch (error) {
    const errorMessage =
      error?.response?.message || t('LIBRARIES.ADD.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const handleClose = () => emit('close');

const onCancel = () => {
  resetForm();
  emit('cancel');
  emit('close');
};

const handleSubmit = () => {
  addKnowledgebase();
};

const handleEscape = e => {
  if (props.active && e.key === 'Escape') {
    handleClose();
  }
};

onMounted(() => {
  document.addEventListener('keydown', handleEscape);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscape);
});

defineExpose({ resetForm });
</script>

<template>
  <div
    class="w-[25rem] z-50 min-w-0 absolute top-10 ltr:right-0 rtl:left-0 bg-n-alpha-3 backdrop-blur-[100px] p-6 rounded-xl border border-slate-50 dark:border-slate-900 shadow-md flex flex-col gap-6 max-h-[85vh] overflow-y-auto"
  >
    <h3 class="text-base font-medium text-slate-900 dark:text-slate-50">
      {{ t('LIBRARIES.ADD.NAME') }}
    </h3>

    <form
      class="flex flex-col w-full"
      @submit.prevent="handleSubmit"
      @keydown.enter.prevent="handleSubmit"
    >
      <div class="w-full">
        <Input
          v-model="name"
          data-testid="knowledgebase-name-input"
          :label="t('LIBRARIES.ADD.FORM.NAME.LABEL')"
          type="text"
          autofocus
          :message="v$.name.$error ? t('LIBRARIES.ADD.FORM.NAME.ERROR') : ''"
          :message-type="v$.name.$error ? 'error' : 'info'"
          :placeholder="t('LIBRARIES.ADD.FORM.NAME.PLACEHOLDER')"
          @blur="v$.name.$touch"
        />
      </div>

      <div class="w-full">
        <Input
          v-model="description"
          data-testid="knowledgebase-description-input"
          :label="t('LIBRARIES.ADD.FORM.DESCRIPTION.LABEL')"
          type="text"
          :message="
            v$.description.$error
              ? t('LIBRARIES.ADD.FORM.DESCRIPTION.ERROR')
              : ''
          "
          :message-type="v$.description.$error ? 'error' : 'info'"
          :placeholder="t('LIBRARIES.ADD.FORM.DESCRIPTION.PLACEHOLDER')"
          @blur="v$.description.$touch"
        />
      </div>

      <input v-model="type" type="hidden" />
      <div class="w-full mb-4">
        <label>{{ t('LIBRARIES.ADD.FORM.TYPE.LABEL') }}</label>
        <div class="flex grid grid-cols-2 sm:grid-cols-4 gap-1 mt-2 justify-between">
          <div
            class="flex flex-col items-center justify-center w-full h-24 border-2 rounded-lg cursor-pointer transition-colors font-semibold border-woot-100 hover:border-woot-300 text-woot-400 hover:text-woot-600"
            :class="[{ 'border-woot-600 text-woot-600': type === 'documents' }]"
            data-testid="kb-type-documents"
            @click="type = 'documents'"
          >
            <Icon icon="i-lucide-file h-8 w-8 color-woot-600 opacity-50" />
            <span class="text-sm text-center">{{
              t('LIBRARIES.ADD.FORM.TYPE.DOCUMENT_LIBRARY')
            }}</span>
          </div>
          <div
            v-if="eventDocumentsEnabled"
            class="flex flex-col items-center justify-center w-full h-24 border-2 rounded-lg cursor-pointer transition-colors font-semibold border-woot-100 hover:border-woot-300 text-woot-400 hover:text-woot-600"
            :class="[{ 'border-woot-600 text-woot-600': type === 'event_documents' }]"
            data-testid="kb-type-event-documents"
            @click="type = 'event_documents'"
          >
            <Icon icon="i-lucide-file-signature h-8 w-8 opacity-50" />
            <span class="text-sm text-center">{{
              t('LIBRARIES.ADD.FORM.TYPE.EVENT_DOCUMENTS')
            }}</span>
          </div>
          <div
            class="flex flex-col items-center justify-center w-full h-24 border-2 rounded-lg cursor-pointer transition-colors font-semibold border-woot-100 hover:border-woot-300 text-woot-400 hover:text-woot-600"
            :class="[{ 'border-woot-600 text-woot-600': type === 'catalog' }]"
            data-testid="kb-type-catalog"
            @click="type = 'catalog'"
          >
            <Icon icon="i-lucide-layout-grid h-8 w-8 opacity-50" />
            <span class="text-sm text-center">{{
              t('LIBRARIES.ADD.FORM.TYPE.CATALOG')
            }}</span>
          </div>
          <div
            class="flex flex-col items-center justify-center w-full h-24 border-2 rounded-lg cursor-pointer transition-colors font-semibold border-woot-100 hover:border-woot-300 text-woot-400 hover:text-woot-600"
            :class="[{ 'border-woot-600 text-woot-600': type === 'gallery' }]"
            data-testid="kb-type-gallery"
            @click="type = 'gallery'"
          >
            <Icon icon="i-lucide-book-image h-8 w-8 opacity-50" />
            <span class="text-sm text-center">{{
              t('LIBRARIES.ADD.FORM.TYPE.GALLERY')
            }}</span>
          </div>
        </div>

      </div>

      <!-- Advanced Settings Toggle -->
      <div class="mt-4">
        <button
          type="button"
          class="flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200 cursor-pointer transition-colors duration-200"
          :aria-expanded="showAdvanced"
          aria-controls="advanced-settings"
          @click="showAdvanced = !showAdvanced"
        >
          <span>
            {{
              showAdvanced
                ? $t('LIBRARIES.ADD.ADVANCED_SETTINGS.TOGGLE_HIDE')
                : $t('LIBRARIES.ADD.ADVANCED_SETTINGS.TOGGLE_SHOW')
            }}
          </span>
          <Icon
            :icon="
              showAdvanced ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'
            "
            class="w-4 h-4"
          />
        </button>
      </div>

      <!-- Advanced Settings -->
      <div v-if="showAdvanced" id="advanced-settings" class="mt-4 space-y-4">
        <!-- Content -->
        <div
          class="p-4 border border-slate-200 dark:border-slate-700 rounded-lg"
        >
          <h4
            class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3"
          >
            {{ $t('LIBRARIES.ADD.ADVANCED_SETTINGS.CONTENT.TITLE') }}
          </h4>
          <div>
            <label class="flex items-center gap-2 cursor-pointer">
              <input v-model="indexMedia" type="checkbox" />
              <span class="text-sm text-slate-700 dark:text-slate-300">{{
                $t('LIBRARIES.ADD.ADVANCED_SETTINGS.INDEX_MEDIA.LABEL')
              }}</span>
            </label>
            <p class="pl-6 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {{
                $t('LIBRARIES.ADD.ADVANCED_SETTINGS.INDEX_MEDIA.DESCRIPTION')
              }}
            </p>
          </div>
        </div>

        <!-- Search -->
        <div
          class="p-4 border border-slate-200 dark:border-slate-700 rounded-lg"
        >
          <h4
            class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3"
          >
            {{ $t('LIBRARIES.ADD.ADVANCED_SETTINGS.SEARCH.TITLE') }}
          </h4>

          <QueryBuilderInstructionEditor
            v-model="queryBuilderInstructions"
            :label="
              $t(
                'LIBRARIES.ADD.ADVANCED_SETTINGS.QUERY_BUILDER_INSTRUCTIONS.LABEL'
              )
            "
            :placeholder="
              $t(
                'LIBRARIES.ADD.ADVANCED_SETTINGS.QUERY_BUILDER_INSTRUCTIONS.PLACEHOLDER'
              )
            "
            :has-error="v$.queryBuilderInstructions.$error"
            :error-message="
              $t(
                'LIBRARIES.ADD.ADVANCED_SETTINGS.QUERY_BUILDER_INSTRUCTIONS.ERROR'
              )
            "
            @blur="v$.queryBuilderInstructions.$touch"
          />

          <div class="mt-4">
            <label class="flex items-center gap-2 cursor-pointer">
              <input v-model="optimizedQuery" type="checkbox" />
              <span class="text-sm text-slate-700 dark:text-slate-300">{{
                $t('LIBRARIES.ADD.ADVANCED_SETTINGS.OPTIMIZED_QUERY.LABEL')
              }}</span>
            </label>
            <p class="pl-6 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {{
                $t(
                  'LIBRARIES.ADD.ADVANCED_SETTINGS.OPTIMIZED_QUERY.DESCRIPTION'
                )
              }}
            </p>
          </div>

          <div class="mt-3">
            <label class="flex items-center gap-2 cursor-pointer">
              <input v-model="disableMatchFilter" type="checkbox" />
              <span class="text-sm text-slate-700 dark:text-slate-300">{{
                $t('LIBRARIES.ADD.ADVANCED_SETTINGS.DISABLE_MATCH_FILTER.LABEL')
              }}</span>
            </label>
            <p class="pl-6 text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              {{
                $t(
                  'LIBRARIES.ADD.ADVANCED_SETTINGS.DISABLE_MATCH_FILTER.DESCRIPTION'
                )
              }}
            </p>
          </div>

          <div class="mt-5">
            <Slider
              v-model="hybridFactor"
              :min="1.0"
              :max="2.0"
              :step="0.1"
              :label="
                $t('LIBRARIES.ADD.ADVANCED_SETTINGS.HYBRID_FACTOR.LABEL') +
                ' — ' +
                hybridFactorLevel
              "
            />
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {{
                $t('LIBRARIES.ADD.ADVANCED_SETTINGS.HYBRID_FACTOR.DESCRIPTION')
              }}
            </p>
          </div>
        </div>

        <!-- Location -->
        <div
          class="p-4 border border-slate-200 dark:border-slate-700 rounded-lg"
        >
          <h4
            class="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3"
          >
            {{ $t('LIBRARIES.ADD.ADVANCED_SETTINGS.LOCATION.TITLE') }}
          </h4>

          <div>
            <label
              class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
            >
              {{ $t('LIBRARIES.ADD.ADVANCED_SETTINGS.DEFAULT_GEO_MODE.LABEL') }}
            </label>
            <select
              v-model="defaultGeoMode"
              class="reset-base w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              <option :value="null">
                {{
                  $t(
                    'LIBRARIES.ADD.ADVANCED_SETTINGS.DEFAULT_GEO_MODE.OPTIONS.DEFAULT'
                  )
                }}
              </option>
              <option value="isochrone">
                {{
                  $t(
                    'LIBRARIES.ADD.ADVANCED_SETTINGS.DEFAULT_GEO_MODE.OPTIONS.ISOCHRONE'
                  )
                }}
              </option>
              <option value="auto">
                {{
                  $t(
                    'LIBRARIES.ADD.ADVANCED_SETTINGS.DEFAULT_GEO_MODE.OPTIONS.AUTO'
                  )
                }}
              </option>
            </select>
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {{
                $t(
                  'LIBRARIES.ADD.ADVANCED_SETTINGS.DEFAULT_GEO_MODE.DESCRIPTION'
                )
              }}
            </p>
          </div>

          <div class="mt-4">
            <Slider
              v-model="maxDistanceBoost"
              :min="0.0"
              :max="1.0"
              :step="0.1"
              :label="
                $t('LIBRARIES.ADD.ADVANCED_SETTINGS.MAX_DISTANCE_BOOST.LABEL') +
                ' — ' +
                maxDistanceBoostLevel
              "
            />
            <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {{
                $t(
                  'LIBRARIES.ADD.ADVANCED_SETTINGS.MAX_DISTANCE_BOOST.DESCRIPTION'
                )
              }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex flex-row justify-end gap-2 py-2 px-0 w-full mt-4">
        <NextButton
          md
          slate
          ghost
          :label="$t('LIBRARIES.ADD.CANCEL_BUTTON_TEXT')"
          @click.prevent="onCancel"
        />
        <NextButton
          type="submit"
          data-testid="knowledgebase-submit-button"
          :label="$t('LIBRARIES.ADD.CREATE_BUTTON_TEXT')"
          :is-loading="store.getters['knowledgebase/getUIFlags'].isCreating"
        />
      </div>
    </form>
  </div>
</template>
